import { Component, inject, input, OnInit, output, signal } from '@angular/core';
import { forkJoin, repeat, retry, takeWhile } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { captureException } from '@sentry/angular';
import { BaseComponent } from 'src/app/components/_base/base.component';
import { AuthService } from 'src/app/services/auth/auth.service';
import { MatIconModule } from '@angular/material/icon';

type InstallMethod = 'embed' | 'block';

declare global {
  interface Window {
    // E2E-only override for the install-status poll timings (see ThemeExtensionComponent).
    __themeExtensionPolling__?: { intervalMs?: number; maxRetries?: number };
  }
}

@Component({
  templateUrl: './theme-extension.component.html',
  standalone: true,
  selector: 'app-onboarding-theme-extension',
  imports: [MatButtonModule, MatProgressSpinnerModule, TranslatePipe, MatIconModule],
  styles: `
    button[mat-button],
    button[mat-flat-button],
    button[mat-stroked-button] {
      --mdc-filled-button-container-height: 36px;
      --mdc-text-button-container-height: 36px;
      --mdc-outlined-button-container-height: 36px;
    }
    button[mat-button] {
      --mdc-text-button-label-text-color: rgb(var(--color-text-6));
    }
  `,
})
export class ThemeExtensionComponent extends BaseComponent implements OnInit {
  private readonly authService = inject(AuthService);

  readonly previousStep = input.required<number>();

  readonly back = output<number>();
  readonly skip = output<void>();
  readonly continue = output<void>();

  readonly embedDeepLink = signal('');
  readonly blockDeepLink = signal('');
  readonly embedLoading = signal(true);
  readonly blockLoading = signal(true);

  readonly embedInstalled = signal(false);
  readonly blockInstalled = signal(false);
  // True until the first check resolves.
  readonly checking = signal(true);

  // Re-check the storefront install status every 3 seconds; the next request only
  // fires once the previous one has finished (repeat resubscribes after completion).
  private static readonly POLL_INTERVAL_MS = 3000;
  // Give up polling after this many consecutive failures so a persistent backend
  // error surfaces instead of retrying forever.
  private static readonly MAX_RETRIES = 5;

  // E2E tests run against the real app and can't override DI providers, so they may
  // shrink these timings via `window.__themeExtensionPolling__` to avoid multi-second
  // waits. The production app never sets it and uses the defaults above.
  private get pollIntervalMs(): number {
    return window.__themeExtensionPolling__?.intervalMs ?? ThemeExtensionComponent.POLL_INTERVAL_MS;
  }
  private get maxRetries(): number {
    return window.__themeExtensionPolling__?.maxRetries ?? ThemeExtensionComponent.MAX_RETRIES;
  }

  ngOnInit(): void {
    this.sub$ = forkJoin({
      embed: this.authService.fetchAppEmbedState(),
      block: this.authService.fetchAppBlockState(),
    })
      .pipe(
        repeat({ delay: this.pollIntervalMs }),
        retry({
          count: this.maxRetries,
          delay: this.pollIntervalMs,
          resetOnSuccess: true,
        }),
        // Emit the result that flips a status to installed, then complete so we stop polling.
        takeWhile(({ embed, block }) => !embed.result && !block.result, true),
      )
      .subscribe({
        next: ({ embed, block }) => {
          this.embedDeepLink.set(embed.deepLink);
          this.blockDeepLink.set(block.deepLink);
          this.embedInstalled.set(embed.result);
          this.blockInstalled.set(block.result);
          this.embedLoading.set(false);
          this.blockLoading.set(false);
          this.checking.set(false);
        },
        error: (error) => {
          captureException(error);
          this.embedLoading.set(false);
          this.blockLoading.set(false);
          this.checking.set(false);
        },
      });
  }

  onSelectMethod(method: InstallMethod) {
    const link = method === 'embed' ? this.embedDeepLink() : this.blockDeepLink();

    if (link) window.open(link, '_blank');
  }

  onBack() {
    this.back.emit(this.previousStep());
  }

  onContinue() {
    this.continue.emit();
  }
}
